<script>
  import { onMount, tick } from "svelte";
  import CloseButton from "./CloseButton.svelte";
  export let src;
  export let onClose;
  let video; // bind:video
  let isIframe = false;
  let isYoutube = false;
  let isVimeo = false;
  let videoContainer;
  let youtubePlayer;
  let videoId = '';
  let uniqueId = `video-${Math.random().toString(36).substr(2, 9)}`;

  function loadYouTubeAPI() {
    return new Promise((resolve) => {
      if (!window.YT) { // Only load if not already loaded
        if (process.env.NODE_ENV === 'development') {
          console.log('Loading YouTube IFrame API...');
        }
        const tag = document.createElement('script');
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        // Set up the callback before loading the API
        window.onYouTubeIframeAPIReady = () => {
          if (process.env.NODE_ENV === 'development') {
            console.log('YouTube API ready callback fired');
          }
          resolve();
        };
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('YouTube IFrame API already loaded');
        }
        resolve();
      }
    });
  }

  async function setupYouTubePlayer() {
    // First ensure API is loaded
    await loadYouTubeAPI();
    
    // Then initialize the player
    if (!window.YT || !window.YT.Player) {
      if (process.env.NODE_ENV === 'development') {
        console.log('YouTube API not ready yet, retrying...');
      }
      setTimeout(setupYouTubePlayer, 100);
      return;
    }

    // Always clean up existing player before creating a new one
    if (youtubePlayer) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Destroying existing YouTube player');
      }
      youtubePlayer.destroy();
      youtubePlayer = null;
    }

    if (!videoId) {
      console.warn('No video ID available');
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Initializing YouTube player for video:', videoId);
    }
    youtubePlayer = new YT.Player(uniqueId, {
      videoId,
      playerVars: {
        // Add parameters to minimize tracking and CORS issues
        enablejsapi: 1,
        origin: window.location.origin,
        widget_referrer: window.location.origin,
        autoplay: 1,
        rel: 0,           // Don't show related videos
        modestbranding: 1 // Minimal YouTube branding
      },
      events: {
        onReady: (event) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('YouTube player ready');
          }
        },
        onStateChange: (event) => {
          // Debug
          if (process.env.NODE_ENV === 'development') {
            const states = {
              '-1': 'unstarted',
              '0': 'ended',
              '1': 'playing',
              '2': 'paused',
              '3': 'buffering',
              '5': 'video cued'
            };
            console.log('Player state changed to:', states[event.data]);
          }

          if (event.data === YT.PlayerState.ENDED) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Video ended, closing player');
            }
            close();
          }
        },
        onError: (event) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('YouTube player error:', event.data);
          }
        }
      }
    });
  }

  $: if (/youtube/i.test(src)) {
    isYoutube = true;
    isIframe = true;
    // Extract video ID and initialize
    const match = src.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
    videoId = match ? match[1] : '';
    if (videoId) {
      setupYouTubePlayer();
    }
  } else if (/vimeo/i.test(src)) {
    isVimeo = true;
    isIframe = true;
  } else {
    isIframe = false;
  }

  // Remove the onMount handler since we're handling initialization in loadYouTubeAPI
  onMount(() => {
    // Clean up YouTube player on component destroy
    return () => {
      if (youtubePlayer) {
        youtubePlayer.destroy();
        youtubePlayer = null;
      }
    };
  });

  async function focusVideo() {
    await tick();
    videoContainer.focus();
  }

  // TODO Implement focus trap
  // $: if (src) {
  //   focusVideo();
  // }

  import UA from "ua-parser-js";
  import { focusTrap } from "./utils";
  const ua = new UA();
  let mobile = ua.getResult().device["type"] == "mobile";

  function close() {
    if (youtubePlayer) {
      youtubePlayer.destroy();
      youtubePlayer = null;
    }
    src = false;
    if (onClose) {
      onClose();
    }
  }

  function fullscreen() {
    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.mozRequestFullScreen) {
      video.mozRequestFullScreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  }
</script>

<!-- /*____________ Markup ____________*/ -->
{#if src}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="background" on:click={close}></div>
  <div class="container" class:isMobile={mobile}>
    <header class="video-header">
      <CloseButton {close} useAbsolutePosition={false} />
    </header>
    <div class="video-container">
      {#if isIframe}
        <iframe
          id={uniqueId}
          title="Video"
          {src}
          allowfullscreen
          allow="autoplay"
          frameborder="0"
          bind:this={videoContainer}
        />
      {:else}
        <!-- svelte-ignore a11y-media-has-caption -->
        <video id={uniqueId} controls autoplay bind:this={video}>
          <source {src} type="video/mp4" />
        </video>
      {/if}
    </div>
  </div>
{/if}

<!-- /*____________ CSS ____________*/ -->
<style>
  .background {
    background: #000000;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99101;
  }

  .container {
    display: grid;
    grid-template-rows: auto 1fr;
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    z-index: 99102;
  }

  .video-header {
    grid-row: 1;
    display: flex;
    justify-content: flex-end;
    padding: 0.5em;
  }

  .video-container {
    grid-row: 2;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
    margin: auto;
    overflow: hidden;
  }

  video,
  iframe {
    width: 100vw;
    height: 56.25vw; /* height:width ratio = 9/16 = .5625  */
    max-height: calc(100vh - 100px); /* close button height */
    max-width: 177.78vh; /* 16/9 = 1.778 */
    margin: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  /* landscape mobile */
  @media (orientation: landscape) {
    .isMobile .video-header {
      position: absolute;
      right: 0;
      top: 0;
      background: #000000;
      z-index: 999999;
      padding: 0.5em 0.75em 0.15em 0.75em;
    }

    .isMobile video,
    .isMobile iframe {
      height: 100vh;
      width: 100vw;
      max-width: 100vw;
      max-height: 100vh;
    }
  }
</style>
